/**
 * SaveContactForm — shared inline form for adding a caller to the
 * Contacts directory. Used by:
 *   • PostCallDocumentationModal (fires after a call ≥3s)
 *   • ActiveCallOverlay's "Save Contact" pill (fires DURING a live call)
 *
 * Duplicate prevention (iter-430): as soon as the form mounts we check
 * ``/api/contacts/by-phone`` and, if the number is already saved, show
 * an amber warning banner listing every existing match. The agent can:
 *   • "Open contact"      → navigate to /contacts/{id}
 *   • "Save as new anyway" → dismiss the banner and continue with the form
 *
 * Props:
 *   • phone            — pre-filled caller phone number (read-only in UI)
 *   • onCancel()       — parent closes the form
 *   • onSaved(contact) — parent handles success (usually closes with toast)
 *   • initialName      — pre-fill "First Last" if agent typed into a
 *                        larger form first; parsed loosely
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, Save, ExternalLink, AlertTriangle, X,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const authHdr = () => {
  const t = localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function formatPhone(raw) {
  if (!raw) return "—";
  const d = String(raw).replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) {
    return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  if (d.length === 10) {
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return raw;
}

export default function SaveContactForm({ phone, onCancel, onSaved, initialName = "" }) {
  const parsed = initialName.trim().split(/\s+/);
  const [firstName, setFirstName] = useState(parsed[0] || "");
  const [lastName, setLastName] = useState(parsed.slice(1).join(" "));
  const [company, setCompany] = useState("");
  const [notes, setNotes] = useState("");
  const [contactType, setContactType] = useState("other");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Duplicate-check state — populated on mount when we have a phone.
  const [dupes, setDupes] = useState([]);
  const [dupesChecked, setDupesChecked] = useState(false);
  const [dupesDismissed, setDupesDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    if (!phone) { setDupesChecked(true); return; }
    (async () => {
      try {
        const r = await fetch(
          `${API}/api/contacts/by-phone?phone=${encodeURIComponent(phone)}`,
          { headers: authHdr() }
        );
        if (r.ok && !cancelled) {
          const j = await r.json();
          setDupes(j.items || []);
        }
      } catch { /* silent — offline just skips the warning */ }
      finally { if (!cancelled) setDupesChecked(true); }
    })();
    return () => { cancelled = true; };
  }, [phone]);

  const openExisting = (c) => {
    onCancel && onCancel();
    navigate(`/contacts/${c.id}`);
  };

  const save = async () => {
    const fn = firstName.trim();
    const ln = lastName.trim();
    if (!fn && !ln) {
      setError("Enter at least a first or last name");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/contacts`, {
        method: "POST",
        headers: { ...authHdr(), "Content-Type": "application/json" },
        body: JSON.stringify({
          // Backend requires both fields; pass empty strings when the
          // user only supplied one.
          first_name: fn,
          last_name: ln,
          phone_number: phone || "",
          company: company.trim() || null,
          notes: notes.trim() || null,
          contact_type: contactType,
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Save failed");
      }
      const c = await r.json();
      onSaved && onSaved(c);
    } catch (e) {
      setError(e.message || "Couldn't save contact");
    } finally {
      setSaving(false);
    }
  };

  // Show the amber duplicate banner ONLY once the check is done + we
  // found matches + the agent hasn't explicitly dismissed it.
  const showDupeBanner = dupesChecked && dupes.length > 0 && !dupesDismissed;

  return (
    <div data-testid="save-contact-form">
      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={onCancel}
          className="p-1 rounded hover:bg-slate-100 text-slate-500"
          data-testid="save-contact-back"
          aria-label="Back"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <div className="text-[11px] font-black uppercase tracking-widest text-slate-500">Save as new contact</div>
          <div className="text-[11px] text-slate-500 font-mono">{formatPhone(phone)}</div>
        </div>
      </div>

      {showDupeBanner && (
        <div
          className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-2.5"
          data-testid="save-contact-duplicate-banner"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-black text-amber-900">
                This number is already saved in Contacts
              </div>
              <div className="mt-1 space-y-1">
                {dupes.map((c) => {
                  const name = `${c.first_name || ""} ${c.last_name || ""}`.trim() || "(no name)";
                  return (
                    <div
                      key={c.id}
                      className="flex items-center gap-2 text-[12px]"
                      data-testid={`duplicate-contact-${c.id}`}
                    >
                      <span className="flex-1 min-w-0 truncate text-amber-900 font-semibold">
                        {name}
                        {c.company && <span className="text-amber-700 font-normal"> · {c.company}</span>}
                      </span>
                      <button
                        onClick={() => openExisting(c)}
                        className="text-[10.5px] font-black uppercase tracking-widest text-amber-800 hover:text-amber-900 flex items-center gap-1 px-2 py-0.5 rounded hover:bg-amber-100"
                        data-testid={`duplicate-open-${c.id}`}
                      >
                        <ExternalLink className="w-3 h-3" /> Open
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
            <button
              onClick={() => setDupesDismissed(true)}
              className="p-1 rounded hover:bg-amber-100 text-amber-700 shrink-0"
              data-testid="save-contact-duplicate-dismiss"
              aria-label="Save as new anyway"
              title="Save as new anyway"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 mb-2">
        <label className="col-span-1">
          <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">First name</span>
          <input
            autoFocus
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="mt-1 w-full h-9 px-2.5 text-sm rounded-lg border-2 border-slate-200 focus:border-slate-400 outline-none"
            placeholder="Jane"
            data-testid="save-contact-first-name"
          />
        </label>
        <label className="col-span-1">
          <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">Last name</span>
          <input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="mt-1 w-full h-9 px-2.5 text-sm rounded-lg border-2 border-slate-200 focus:border-slate-400 outline-none"
            placeholder="Doe"
            data-testid="save-contact-last-name"
          />
        </label>
      </div>

      <label className="block mb-2">
        <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">Company / practice (optional)</span>
        <input
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          className="mt-1 w-full h-9 px-2.5 text-sm rounded-lg border-2 border-slate-200 focus:border-slate-400 outline-none"
          placeholder="Kaiser Prior Auth"
          data-testid="save-contact-company"
        />
      </label>

      <label className="block mb-2">
        <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">Contact type</span>
        <select
          value={contactType}
          onChange={(e) => setContactType(e.target.value)}
          className="mt-1 w-full h-9 px-2 text-sm rounded-lg border-2 border-slate-200 focus:border-slate-400 outline-none bg-white"
          data-testid="save-contact-type"
        >
          <option value="other">Other</option>
          <option value="client">Client / patient</option>
          <option value="staff">Staff</option>
          <option value="provider">Provider (MD/RN)</option>
          <option value="org">Organization</option>
        </select>
      </label>

      <label className="block">
        <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">Notes (optional)</span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="mt-1 w-full px-2.5 py-1.5 text-sm rounded-lg border-2 border-slate-200 focus:border-slate-400 outline-none resize-y"
          placeholder="Called re: prior auth for John Smith…"
          data-testid="save-contact-notes"
        />
      </label>

      {error && (
        <div className="mt-2 text-[11px] text-red-600 font-semibold" data-testid="save-contact-error">
          {error}
        </div>
      )}

      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-[11px] font-bold rounded-md text-slate-500 hover:bg-slate-100"
          data-testid="save-contact-cancel"
        >
          Cancel
        </button>
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-1.5 text-[11px] font-black rounded-md shadow-sm hover:shadow-md transition disabled:opacity-50 flex items-center gap-1.5"
          style={{ background: "#032866", color: "#FFDA00" }}
          data-testid="save-contact-submit"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save contact
        </button>
      </div>
    </div>
  );
}
